import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import useAuth from 'src/hooks/useAuth';
import { Schema } from 'src/utils/form';
import Results from 'src/componentes/NewList/Results';
import Input from 'src/componentes/input';
import Button from 'src/componentes/button';
import InputSearch from 'src/componentes/input-search';
import Checkbox from 'src/componentes/checkbox';
import PanelCollapse from 'src/componentes/panel-collapse';
import FluxoAprovacaoGenericoLookup from 'src/lookups/FluxoAprovacaoGenericoLookup';
import StatusItemLookup from 'src/lookups/status-item';
import Edit from 'src/componentes/edit/edit-dialog';
import { capitalizeFirstLetter } from 'src/utils/string';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import InfoPanel from 'src/componentes/info-panel';
import LucideIcon from 'src/componentes/lucide-icon';
import { TIPO_ITENS, TIPO_FLUXO_APROVACAO } from 'src/utils/constants';
import AnexosEtapaEditView from './Anexos/AnexosEtapaEditView';
import CustomInput from 'src/componentes/CustomInput';

const schema = Schema.object({
  nome: Schema.string().required(),
});

const EditForm = ({ model }) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const [customFields, setCustomFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState({});
  const [attributeSearch, setAttributeSearch] = useState({});
  const [attachmentSearch, setAttachmentSearch] = useState({});
  const [stageErrors, setStageErrors] = useState({});

  const [
    performanceTrafficLightRangeEnabled,
    setPerformanceTrafficLightRangeEnabled,
  ] = useState(true);
  const [teamFeatureEnabled, setTeamFeatureEnabled] = useState(false);

  const allowEdit =
    model &&
    model.value &&
    model.value.permissions &&
    model.value.permissions.allowEdit;

  // Campos que dependem de configuração
  const configDependentFieldIds = ['faixaDeFarol', 'equipe'];

  const [defaultFields] = useState([
    {
      id: 'nome',
      descricao: capitalizeFirstLetter(intl.formatMessage({ id: 'nome' })),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'idIntegracao',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'idIntegracao' })
      ),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'area',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'area' }, { area: resources.area })
      ),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'responsaveis',
      descricao: capitalizeFirstLetter(resources.responsaveisprojeto),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'status',
      descricao: capitalizeFirstLetter(intl.formatMessage({ id: 'status' })),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'dataInicioItem',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'dataInicio' })
      ),
      obrigatorio: true,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'dataTerminoItem',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'dataTermino' })
      ),
      obrigatorio: false,
      utilizar: true,
      disabled: true,
    },
    {
      id: 'equipe',
      descricao: capitalizeFirstLetter(intl.formatMessage({ id: 'equipe' })),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'escopo',
      descricao: capitalizeFirstLetter(intl.formatMessage({ id: 'escopo' })),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'subProjetos',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'subprojetos' })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'tags',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'tags' }, { tags: resources.tags })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'faixaDeFarol',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'faixaDeFarol' })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
  ]);

  const [defaultAttributes] = useState([
    {
      id: 'tap',
      descricao: capitalizeFirstLetter(resources.tap),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'canvas',
      descricao: capitalizeFirstLetter(intl.formatMessage({ id: 'canvas' })),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'analiseStakeholders',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'analiseStakeholders' })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'eapGrafica',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'eapGrafica' })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'matrizResponsabilidades',
      descricao: capitalizeFirstLetter(
        intl.formatMessage({ id: 'matrizResponsabilidades' })
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
    {
      id: 'planoDeAcao',
      descricao: capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'planoDeAcao' },
          { planodeacao: resources.planodeacao }
        )
      ),
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    },
  ]);

  const getFullFieldsTemplate = () => {
    const baseTemplate = [...defaultFields];

    const custom = (customFields || []).map((cf) => ({
      id: String(cf.campo?.id),
      descricao: cf.campo?.nome,
      obrigatorio: false,
      utilizar: false,
      disabled: false,
    }));

    const templateWithDynamicDisabled = baseTemplate.map((field) => {
      if (field.id === 'faixaDeFarol') {
        return {
          ...field,
          disabled: !performanceTrafficLightRangeEnabled,
        };
      } else if (field.id === 'equipe') {
        return {
          ...field,
          disabled: !teamFeatureEnabled,
        };
      }

      return field;
    });

    return [...templateWithDynamicDisabled, ...custom];
  };

  const getAvailableFieldsForStage = (stageIndex) => {
    const stages = model.getModel('etapas').value || [];
    const isFirstStage = stageIndex === 0;
    const usedFieldIds = new Set();

    stages.forEach((etapa, idx) => {
      if (idx === stageIndex) return;
      let campos = [];
      try {
        campos = JSON.parse(etapa.camposEtapaJSON || '[]');
      } catch {}
      campos.forEach((f) => {
        if (f.utilizar) usedFieldIds.add(String(f.id));
      });
    });

    const baseTemplate = getFullFieldsTemplate();

    return baseTemplate.filter((field) => {
      const fieldId = String(field.id);
      if (field.disabled && !isFirstStage) return false;
      if (usedFieldIds.has(fieldId)) return false;
      return true;
    });
  };

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const response = await http.post(
          `/CampoCustomizado/ObterListaCamposCustomizados`,
          { tipoItem: TIPO_ITENS.PROJETO }
        );
        setCustomFields(response.data || []);
      } catch (err) {
        errorHandler(err);
      }
    };
    loadCustomFields();
  }, []);

  // Atualiza estado de cmapos que dependem de configuração
  useEffect(() => {
    const configModel = model.getModel('configuracaoGeral');
    if (!configModel?.value) {
      setPerformanceTrafficLightRangeEnabled(true);
      setTeamFeatureEnabled(false);
      return;
    }

    //Quando não estiver usando farol de desempenho por faixa o campo faixa de farol fica habilitado
    const trafficLightByRangeEnabled =
      !!configModel.value.utilizarFarolDesempenhoFaixa;
    setPerformanceTrafficLightRangeEnabled(trafficLightByRangeEnabled);

    setTeamFeatureEnabled(!!configModel.value.habilitarEquipe);

    const stages = model.getModel('etapas').value || [];
    if (stages.length === 0) return;

    const forceUtilizarFalseFields = new Set();
    if (trafficLightByRangeEnabled)
      forceUtilizarFalseFields.add('faixaDeFarol');
    if (teamFeatureEnabled) forceUtilizarFalseFields.add('equipe');

    let needsUpdate = false;
    const updatedStages = stages.map((etapa) => {
      let campos = [];
      try {
        campos = JSON.parse(etapa.camposEtapaJSON || '[]');
      } catch {
        campos = [];
      }

      const updatedCampos = campos.map((c) => {
        if (forceUtilizarFalseFields.has(c.id) && c.utilizar) {
          needsUpdate = true;
          // Força desmarcação dos campos quando a configuração os ativa
          return { ...c, utilizar: false };
        }
        return c;
      });

      if (needsUpdate || updatedCampos.some((uc, i) => uc !== campos[i])) {
        return { ...etapa, camposEtapaJSON: JSON.stringify(updatedCampos) };
      }
      return etapa;
    });

    if (needsUpdate) {
      model.getModel('etapas').requestChange(updatedStages);
    }
  }, [model.getModel('configuracaoGeral')?.value]);

  useEffect(() => {
    const stages = model.getModel('etapas').value || [];
    if (stages.length === 0) return;

    let needsUpdate = false;
    const updatedStages = stages.map((etapa, index) => {
      let campos = [];
      try {
        campos = JSON.parse(etapa.camposEtapaJSON || '[]');
      } catch {
        campos = [];
      }

      if (campos.length === 0) {
        const available = getAvailableFieldsForStage(index);

        if (available.length === 0) {
          return etapa;
        }

        const fixed = available.map((f) => ({
          id: f.id,
          descricao: f.descricao,
          utilizar:
            index === 0 && f.disabled && !configDependentFieldIds.includes(f.id)
              ? true
              : false,
          obrigatorio: f.obrigatorio || false,
        }));
        needsUpdate = true;
        return { ...etapa, camposEtapaJSON: JSON.stringify(fixed) };
      }

      if (index === 0) {
        const corrected = campos.map((c) => {
          const isDisabledDefault = defaultFields.some(
            (df) => df.id === c.id && df.disabled
          );
          if (
            isDisabledDefault &&
            !c.utilizar &&
            !configDependentFieldIds.includes(c.id)
          ) {
            needsUpdate = true;
            return { ...c, utilizar: true };
          }
          return c;
        });
        if (needsUpdate)
          return { ...etapa, camposEtapaJSON: JSON.stringify(corrected) };
      }

      return etapa;
    });

    if (needsUpdate) {
      model.getModel('etapas').requestChange(updatedStages);
    }
  }, [model.getModel('etapas').value]);

  useEffect(() => {
    const stages = model.getModel('etapas').value || [];
    const errors = {};

    stages.forEach((etapa, index) => {
      if (!etapa.nome || etapa.nome.trim() === '') {
        errors[index] = { ...errors[index], nome: 'required' };
      }
      if (!etapa.fluxoAprovacao) {
        errors[index] = { ...errors[index], fluxoAprovacao: 'required' };
      }
      if (!etapa.statusProjeto) {
        errors[index] = { ...errors[index], statusProjeto: 'required' };
      }

      let atributos = [];
      try {
        atributos = JSON.parse(etapa.atributosEtapaJSON || '[]');
      } catch {}
      if (!atributos.some((a) => a.utilizar)) {
        errors[index] = { ...errors[index], atributos: 'required' };
      }

      let campos = [];
      try {
        campos = JSON.parse(etapa.camposEtapaJSON || '[]');
      } catch {}
      if (!campos.some((c) => c.utilizar)) {
        errors[index] = { ...errors[index], campos: 'required' };
      }
    });

    setStageErrors(errors);
  }, [model.getModel('etapas').value]);

  const mergeWithSaved = (template, savedJson) => {
    if (!savedJson) {
      return template.map((f) => ({
        ...f,
        utilizar:
          f.disabled && !configDependentFieldIds.includes(f.id)
            ? true
            : f.utilizar,
      }));
    }

    let saved = [];
    try {
      saved = typeof savedJson === 'string' ? JSON.parse(savedJson) : savedJson;
    } catch {
      saved = [];
    }
    const savedMap = new Map(saved.map((item) => [String(item.id), item]));

    return template.map((templateItem) => {
      const savedItem = savedMap.get(String(templateItem.id));
      const merged = savedItem
        ? { ...templateItem, ...savedItem }
        : { ...templateItem };

      if (
        templateItem.disabled &&
        !configDependentFieldIds.includes(templateItem.id)
      ) {
        merged.utilizar = true;
      }

      // if (nonMandatoryIds.includes(merged.id)) {
      //   merged.obrigatorio = false;
      // }
      return merged;
    });
  };

  const getAvailableAttributesForStage = (stageIndex) => {
    const stages = model.getModel('etapas').value || [];
    const usedAttributeIds = new Set();

    stages.forEach((etapa, idx) => {
      if (idx === stageIndex) return;
      let atributos = [];
      try {
        atributos = JSON.parse(etapa.atributosEtapaJSON || '[]');
      } catch {}
      atributos.forEach((a) => {
        if (a.utilizar) usedAttributeIds.add(String(a.id));
      });
    });

    return defaultAttributes.filter((attr) => {
      return !usedAttributeIds.has(String(attr.id));
    });
  };

  const handleFieldChange = (stageIndex, type, fieldId, property, value) => {
    const stages = [...(model.getModel('etapas').value || [])];
    const stage = stages[stageIndex];
    const jsonStr =
      type === 'campos'
        ? stage.camposEtapaJSON
        : type === 'atributos'
        ? stage.atributosEtapaJSON
        : stage.anexosEtapaJSON;
    let items = [];
    if (jsonStr) {
      try {
        items = JSON.parse(jsonStr);
      } catch {
        items = [];
      }
    }
    let item = items.find((i) => String(i.id) === String(fieldId));
    if (!item) {
      item = { id: fieldId, utilizar: false, obrigatorio: false };
      items.push(item);
    }

    if (property === 'utilizar' && value === false && item.obrigatorio) {
      item.obrigatorio = false;
    }

    if (property === 'obrigatorio' && value === true && !item.utilizar) {
      return;
    }

    item[property] = value;

    const newJson = JSON.stringify(items);
    if (type === 'campos') stage.camposEtapaJSON = newJson;
    else if (type === 'atributos') stage.atributosEtapaJSON = newJson;
    else if (type === 'anexos') stage.anexosEtapaJSON = newJson;
    model.getModel('etapas').requestChange(stages);
  };

  const addStage = () => {
    const stages = [...(model.getModel('etapas').value || [])];
    const newIndex = stages.length;

    const availableFields = getAvailableFieldsForStage(newIndex);
    const initializedFields = availableFields.map((field) => ({
      id: field.id,
      descricao: field.descricao,
      utilizar:
        newIndex === 0 &&
        field.disabled &&
        !configDependentFieldIds.includes(field.id)
          ? true
          : false,
      obrigatorio: field.obrigatorio || false,
    }));

    const availableAttributes = getAvailableAttributesForStage(newIndex);
    const initializedAttributes = availableAttributes.map((attr) => ({
      id: attr.id,
      descricao: attr.descricao,
      utilizar: false,
      obrigatorio: false,
    }));

    const newStage = {
      nome: '',
      camposEtapaJSON: JSON.stringify(initializedFields),
      atributosEtapaJSON: JSON.stringify(initializedAttributes),
      anexosEtapaJSON: '[]',
      fluxoAprovacao: null,
      statusProjeto: null,
    };

    stages.push(newStage);
    model.getModel('etapas').requestChange(stages);

    setStageErrors((prev) => ({
      ...prev,
      [newIndex]: {
        nome: 'required',
        fluxoAprovacao: 'required',
        statusProjeto: 'required',
        atributos: 'required',
        ...(newIndex > 0 && { campos: 'required' }),
      },
    }));
  };

  const handleDeleteStage = (stageIndex) => {
    const stages = [...model.getModel('etapas').value];
    stages.splice(stageIndex, 1);
    model.getModel('etapas').requestChange(stages);
  };

  const canAddNewStage = () => {
    const stages = model.getModel('etapas').value || [];
    if (stages.length === 0) return true;
    //Só permite adicionar se a última etapa não tiver status de concluído
    return stages[stages.length - 1].statusProjeto?.id !== 3;
  };

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  };

  const renderUtilizar = (field, stageIndex, type, permissions = {}) => (
    <Checkbox
      className="d-flex"
      style={{ height: 21 }}
      disabled={field.disabled || !permissions.allowEdit}
      model={{
        value: field.utilizar,
        requestChange: (value) =>
          handleFieldChange(stageIndex, type, field.id, 'utilizar', value),
      }}
    />
  );

  const renderObrigatorio = (field, stageIndex, type, permissions = {}) => (
    <div className="d-flex justify-content-end" style={{ maxWidth: '60px' }}>
      <CustomInput
        type="switch"
        id={`switch-required-${stageIndex}-${field.id}`}
        checked={field.obrigatorio}
        onChange={() => {
          if (!field.utilizar) {
            return;
          }
          handleFieldChange(
            stageIndex,
            type,
            field.id,
            'obrigatorio',
            !field.obrigatorio
          );
        }}
        disabled={field.disabled || !field.utilizar || !permissions.allowEdit}
      />
    </div>
  );

  const [columns] = useState([
    {
      id: 1,
      visible: true,
      valueFunction: (item, _, stageIndex, type = 'campos', permissions) =>
        renderUtilizar(item, stageIndex, type, permissions),
      style: { maxWidth: 25 },
    },
    {
      id: 2,
      visible: true,
      valueField: 'descricao',
      headerText: capitalizeFirstLetter(
        intl.formatMessage({ id: 'label.campo' })
      ),
      sortField: 'descricao',
    },
    {
      id: 3,
      visible: true,
      valueFunction: (item, _, stageIndex, type = 'campos', permissions) =>
        renderObrigatorio(item, stageIndex, type, permissions),
      headerText: capitalizeFirstLetter(
        intl.formatMessage({ id: 'obrigatorio?' })
      ),
      style: { width: 50 },
    },
  ]);

  const clearErrorAndUpdate = (stageIndex, field, value) => {
    const stageModel = model.getModel('etapas').getModels()[stageIndex];
    stageModel.getModel(field).requestChange(value);
    setStageErrors((prev) => {
      const updated = { ...prev };
      if (updated[stageIndex]) {
        delete updated[stageIndex][field];
        if (Object.keys(updated[stageIndex]).length === 0)
          delete updated[stageIndex];
      }
      return updated;
    });
  };

  const getAllowedStatusIds = (stageIndex) => {
    const stages = model.getModel('etapas').value || [];
    const isLastStage = stageIndex === stages.length - 1;

    const hasActiveInPreviousStages = stages
      .slice(0, stageIndex)
      .some((stage) => stage?.statusProjeto?.id === 2);

    let allowed = [2];

    if (!hasActiveInPreviousStages) {
      allowed = [1, 2];
    }

    if (isLastStage) {
      allowed = [...allowed, 3];
    }

    return allowed;
  };

  const handleStatusChange = (stageIndex, value) => {
    clearErrorAndUpdate(stageIndex, 'statusProjeto', value);
  };

  const handleAddAttachment = (stageIndex) => {
    const handleCreated = (newAttachment) => {
      const stages = [...(model.getModel('etapas').value || [])];
      const stage = stages[stageIndex];
      if (!stage) return;

      let anexos = [];
      try {
        anexos = JSON.parse(stage.anexosEtapaJSON || '[]');
      } catch {
        anexos = [];
      }

      let attachment = { ...newAttachment };
      if (!attachment.id) {
        attachment.id = crypto.randomUUID();
      }
      if (typeof attachment.utilizar === 'undefined') {
        attachment.utilizar = false;
      }
      if (typeof attachment.obrigatorio === 'undefined') {
        attachment.obrigatorio = false;
      }

      anexos.push(newAttachment);
      stage.anexosEtapaJSON = JSON.stringify(anexos);
      model.getModel('etapas').requestChange(stages);
    };

    ContentManager.addContent(
      <AnexosEtapaEditView onAttachmentCreated={handleCreated} />
    );
  };

  return (
    <>
      <Row>
        <Col>
          <Input
            maxLength={500}
            disabled={!allowEdit}
            model={{
              label: intl.formatMessage(
                { id: 'label.nomeStageGate' },
                { stagegate: resources.stagegate }
              ),
              value: model.getModel('nome').value,
              requestChange: model.getModel('nome').requestChange,
            }}
            required
          />
        </Col>
      </Row>

      {model
        .getModel('etapas')
        .getModels()
        .map((stageModel, stageIndex) => {
          const etapa = stageModel.value || {};

          const availableFieldsTemplate =
            getAvailableFieldsForStage(stageIndex);
          const fullFields = mergeWithSaved(
            availableFieldsTemplate,
            etapa.camposEtapaJSON
          );

          const availableAttributesTemplate =
            getAvailableAttributesForStage(stageIndex);
          const fullAttributes = mergeWithSaved(
            availableAttributesTemplate,
            etapa.atributosEtapaJSON
          );

          const fieldSearchTerm = normalizeText(fieldSearch[stageIndex] || '');
          const attributeSearchTerm = normalizeText(
            attributeSearch[stageIndex] || ''
          );

          const attachmentSearchTerm = normalizeText(
            attachmentSearch[stageIndex] || ''
          );

          let anexosList = [];
          try {
            anexosList = JSON.parse(etapa.anexosEtapaJSON || '[]');
          } catch {}
          const filteredAnexos = attachmentSearchTerm
            ? anexosList.filter((a) =>
                normalizeText(a.descricao).includes(attachmentSearchTerm)
              )
            : anexosList;

          const filteredFields = fieldSearchTerm
            ? fullFields.filter((f) =>
                normalizeText(f.descricao).includes(fieldSearchTerm)
              )
            : fullFields;
          const filteredAttributes = attributeSearchTerm
            ? fullAttributes.filter((a) =>
                normalizeText(a.descricao).includes(attributeSearchTerm)
              )
            : fullAttributes;

          const currentError = stageErrors[stageIndex] || {};

          const actions = [
            <span key="spacer" className="flex-grow-1" />,
            <span
              key="delete"
              className="ml-auto d-flex align-items-center mr-2"
            >
              <LucideIcon
                icon="delete-outline"
                style={{ cursor: 'pointer', fontSize: '20px' }}
                onClick={() => handleDeleteStage(stageIndex)}
              />
            </span>,
          ];

          return (
            <Row key={stageIndex}>
              <Col>
                <PanelCollapse
                  className="mb-2"
                  open
                  header={`${intl.formatMessage({ id: 'etapa' })}: ${
                    etapa.nome || ''
                  }`}
                  headerActions={allowEdit ? actions : []}
                  headerStyle="d-flex align-items-center flex-nowrap"
                >
                  <div>
                    <Row className="mb-2">
                      <Col>
                        <Input
                          maxLength={500}
                          disabled={!allowEdit}
                          model={{
                            label: intl.formatMessage({
                              id: 'nomeEtapaStageGate',
                            }),
                            value: stageModel.getModel('nome').value,
                            errors: currentError.nome
                              ? ['required']
                              : undefined,
                            requestChange: (value) =>
                              clearErrorAndUpdate(stageIndex, 'nome', value),
                          }}
                          required
                        />
                      </Col>
                    </Row>
                    {currentError.campos && currentError.atributos && (
                      <Row className="mb-2">
                        <Col>
                          <InfoPanel showCloseButton={false}>
                            {intl.formatMessage({
                              id: 'mensagemItemListasStageGateRequerido',
                            })}
                          </InfoPanel>
                        </Col>
                      </Row>
                    )}

                    <Row className="mb-2">
                      <Col>
                        <PanelCollapse
                          open
                          header={intl.formatMessage({
                            id: 'camposPreenchidosNaEtapa',
                          })}
                        >
                          <Row className="mb-3">
                            <Col md={4}>
                              <InputSearch
                                placeholder={intl.formatMessage({
                                  id: 'label.pesquisaRapida',
                                })}
                                value={fieldSearch[stageIndex] || ''}
                                onChange={(valueOrEvent) => {
                                  const value =
                                    typeof valueOrEvent === 'string'
                                      ? valueOrEvent
                                      : valueOrEvent?.target?.value || '';
                                  setFieldSearch((prev) => ({
                                    ...prev,
                                    [stageIndex]: value,
                                  }));
                                }}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Results
                                showSearch={true}
                                columns={columns.map((col) => ({
                                  ...col,
                                  valueFunction: col.valueFunction
                                    ? (item) =>
                                        col.valueFunction(
                                          item,
                                          null,
                                          stageIndex,
                                          'campos',
                                          { allowEdit }
                                        )
                                    : undefined,
                                }))}
                                data={filteredFields}
                              />
                            </Col>
                          </Row>
                        </PanelCollapse>
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col>
                        <PanelCollapse
                          open
                          header={intl.formatMessage(
                            { id: 'atributosProjeto' },
                            { projeto: resources.projeto }
                          )}
                        >
                          <Row className="mb-3">
                            <Col md={4}>
                              <InputSearch
                                placeholder={intl.formatMessage({
                                  id: 'label.pesquisaRapida',
                                })}
                                value={attributeSearch[stageIndex] || ''}
                                onChange={(valueOrEvent) => {
                                  const value =
                                    typeof valueOrEvent === 'string'
                                      ? valueOrEvent
                                      : valueOrEvent?.target?.value || '';
                                  setAttributeSearch((prev) => ({
                                    ...prev,
                                    [stageIndex]: value,
                                  }));
                                }}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Results
                                showSearch={true}
                                columns={columns.map((col) => ({
                                  ...col,
                                  valueFunction: col.valueFunction
                                    ? (item) =>
                                        col.valueFunction(
                                          item,
                                          null,
                                          stageIndex,
                                          'atributos',
                                          { allowEdit }
                                        )
                                    : undefined,
                                }))}
                                data={filteredAttributes}
                              />
                            </Col>
                          </Row>
                        </PanelCollapse>
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col>
                        <PanelCollapse
                          open
                          header={intl.formatMessage({ id: 'anexos' })}
                        >
                          <Row className="mb-3">
                            <Col md={4}>
                              <InputSearch
                                placeholder={intl.formatMessage({
                                  id: 'label.pesquisaRapida',
                                })}
                                value={attachmentSearch[stageIndex] || ''}
                                onChange={(valueOrEvent) => {
                                  const value =
                                    typeof valueOrEvent === 'string'
                                      ? valueOrEvent
                                      : valueOrEvent?.target?.value || '';
                                  setAttachmentSearch((prev) => ({
                                    ...prev,
                                    [stageIndex]: value,
                                  }));
                                }}
                              />
                            </Col>
                            <Col>
                              <div className="d-flex justify-content-end">
                                <Button
                                  color="primary"
                                  onClick={() =>
                                    handleAddAttachment(stageIndex)
                                  }
                                  disabled={!allowEdit}
                                >
                                  <LucideIcon icon="plus" />
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Results
                                showSearch={true}
                                columns={columns.map((col) => ({
                                  ...col,
                                  valueFunction: col.valueFunction
                                    ? (item) =>
                                        col.valueFunction(
                                          item,
                                          null,
                                          stageIndex,
                                          'anexos',
                                          { allowEdit }
                                        )
                                    : undefined,
                                }))}
                                data={filteredAnexos}
                              />
                            </Col>
                          </Row>
                        </PanelCollapse>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <FluxoAprovacaoGenericoLookup
                          disabled={!allowEdit}
                          model={{
                            label: intl.formatMessage({ id: 'fluxoAprovacao' }),
                            value: stageModel.getModel('fluxoAprovacao').value,
                            errors: currentError.fluxoAprovacao
                              ? ['required']
                              : undefined,
                            requestChange: (value) =>
                              clearErrorAndUpdate(
                                stageIndex,
                                'fluxoAprovacao',
                                value
                              ),
                          }}
                          data={{
                            tipoFluxoId: TIPO_FLUXO_APROVACAO.STAGE_GATE,
                          }}
                          required
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <StatusItemLookup
                          disabled={!allowEdit}
                          clearable
                          required
                          data={{
                            IdsSelecionados: getAllowedStatusIds(stageIndex),
                          }}
                          model={{
                            label: capitalizeFirstLetter(
                              intl.formatMessage({ id: 'status' })
                            ),
                            value: stageModel.getModel('statusProjeto').value,
                            errors: currentError.statusProjeto
                              ? ['required']
                              : undefined,
                            requestChange: (value) =>
                              handleStatusChange(stageIndex, value),
                          }}
                        />
                        {stageModel.getModel('statusProjeto').value?.id ===
                          3 && (
                          <InfoPanel showCloseButton={false}>
                            {intl.formatMessage({
                              id: 'mensagemStatusConcluidoStageGate',
                            })}
                          </InfoPanel>
                        )}{' '}
                      </Col>
                    </Row>
                  </div>
                </PanelCollapse>
              </Col>
            </Row>
          );
        })}

      {canAddNewStage() && (
        <Row>
          <Col>
            <Button
              style={{ border: 'none', marginLeft: 5 }}
              iconSize={16}
              outline
              leftIcon="plus-circle-outline"
              onClick={addStage}
              disabled={!allowEdit}
            >
              {capitalizeFirstLetter(intl.formatMessage({ id: 'etapa' }))}
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

const EditView = ({ ...rest }) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const handleCustomSave = (data, handleClose) => {
    // Previne múltiplos cliques
    if (isSaving) {
      return;
    }

    const etapas = data.etapas || [];
    let hasError = false;

    if (etapas.length === 0) {
      hasError = true;
    }

    etapas.forEach((etapa) => {
      if (!etapa.nome || etapa.nome.trim() === '') {
        hasError = true;
      }
      if (!etapa.fluxoAprovacao) {
        hasError = true;
      }
      if (!etapa.statusProjeto) {
        hasError = true;
      }

      let atributos = [];
      try {
        atributos = JSON.parse(etapa.atributosEtapaJSON || '[]');
      } catch {}

      let campos = [];
      try {
        campos = JSON.parse(etapa.camposEtapaJSON || '[]');
      } catch {}

      if (
        !atributos.some((a) => a.utilizar) &&
        !campos.some((c) => c.utilizar)
      ) {
        hasError = true;
      }
    });

    if (hasError) {
      etapas.length === 0
        ? ContentManager.addContent(
            <Alert
              title={intl.formatMessage({
                id: 'alerta',
              })}
              message={intl.formatMessage(
                {
                  id: 'validacaoStageGateSemEtapas',
                },
                { stagegate: resources.stagegate }
              )}
              type="error"
            />
          )
        : ContentManager.addContent(
            <Alert
              title={intl.formatMessage({
                id: 'alerta',
              })}
              message={intl.formatMessage({ id: 'camposObrigatoriosEtapas' })}
              type="error"
            />
          );
      return;
    }

    setIsSaving(true);
    http
      .post('/StageGate/Save', { model: data })
      .then(() => {
        ContentManager.addContent(
          <Alert
            title={intl.formatMessage({ id: 'sucesso' })}
            message={intl.formatMessage({
              id: 'label.registroSalvoComSucesso',
            })}
          />
        );
        handleClose();
      })
      .catch((error) => {
        errorHandler(error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Edit
      url="/StageGate"
      title={capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'stagegate' },
          { stagegate: resources.stagegate }
        )
      )}
      formComponent={EditForm}
      schema={schema}
      width="40%"
      maxWidth="40%"
      maxHeight="80%"
      intl={intl}
      handleSave={handleCustomSave}
      {...rest}
      showExport={false}
    />
  );
};

export default EditView;
